/**
 * System messages to customers (D78), for staff with "Manage system messages". Create / edit need
 * a 2FA code; "End now" is one click. Messages are never deleted. The form shows a live preview of
 * the banner exactly as customers will see it, including where a link really goes.
 */
import type { AdminSystemMessage, SystemMessageInput } from "@custos/api-client";
import { checkMessageLink, SYSTEM_MESSAGE_LIMITS } from "@custos/core";
import {
  Badge,
  Button,
  Card,
  EmptyState,
  Field,
  Input,
  Notice,
  Segmented,
  Select,
  Skeleton,
  Spot,
  SystemMessages,
  Textarea,
  formatDateTime,
} from "@custos/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { useEffect, useState } from "react";
import { PageHeader } from "../components/AdminShell";
import { StepUpModal } from "../components/StepUpModal";
import { api } from "../lib/api";
import { errorMessage } from "../lib/errors";

type Status = "ACTIVE" | "SCHEDULED" | "ENDED" | "ALL";
const L = SYSTEM_MESSAGE_LIMITS;

const useMessages = (status: Status) =>
  useQuery({
    queryKey: ["messages", status],
    queryFn: () => api.adminMessages(status),
    refetchInterval: 30_000,
  });

/** "2026-10-22T14:05" in local time for <input type="datetime-local">. */
function toLocalInput(iso: string | Date): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

interface Form {
  title: string;
  body: string;
  level: SystemMessageInput["level"];
  audience: SystemMessageInput["audience"];
  recipients: string;
  linkUrl: string;
  linkLabel: string;
  dismissible: boolean;
  showOnLogin: boolean;
  startsAt: string;
  endsAt: string;
}

const emptyForm = (): Form => ({
  title: "",
  body: "",
  level: "INFO",
  audience: "ALL",
  recipients: "",
  linkUrl: "",
  linkLabel: "",
  dismissible: true,
  showOnLogin: false,
  startsAt: toLocalInput(new Date()),
  endsAt: "",
});

const formOf = (m: AdminSystemMessage): Form => ({
  title: m.title,
  body: m.body,
  level: m.level,
  audience: m.audience,
  recipients: m.recipients.join("\n"),
  linkUrl: m.link?.url ?? "",
  linkLabel: m.link?.label ?? "",
  dismissible: m.dismissible,
  showOnLogin: m.showOnLogin,
  startsAt: toLocalInput(m.startsAt),
  endsAt: m.endsAt ? toLocalInput(m.endsAt) : "",
});

export function MessagesPage() {
  const [status, setStatus] = useState<Status>("ACTIVE");
  const messages = useMessages(status);
  const [editing, setEditing] = useState<AdminSystemMessage | "new" | null>(null);

  return (
    <>
      <PageHeader
        title="Messages"
        subtitle="Notices shown to customers in the app. Critical ones can't be closed."
        actions={
          <Button onClick={() => setEditing("new")}>
            <Plus className="size-4" aria-hidden /> New message
          </Button>
        }
      />
      <Segmented
        label="Which messages"
        value={status}
        onChange={setStatus}
        className="mb-4"
        options={[
          { value: "ACTIVE", label: "Showing now" },
          { value: "SCHEDULED", label: "Scheduled" },
          { value: "ENDED", label: "Ended" },
          { value: "ALL", label: "All" },
        ]}
      />
      {messages.error && <Notice tone="danger">{errorMessage(messages.error)}</Notice>}
      {messages.isPending ? (
        <Skeleton className="h-40 rounded-card" />
      ) : messages.data?.length === 0 ? (
        <Card>
          <EmptyState icon={<Spot name="history" />} title="No messages here" />
        </Card>
      ) : (
        <ul className="stagger grid grid-cols-1 gap-3 lg:grid-cols-2" data-testid="message-list">
          {messages.data?.map((m) => (
            <MessageCard key={m.id} message={m} onEdit={() => setEditing(m)} />
          ))}
        </ul>
      )}
      <MessageModal message={editing} onClose={() => setEditing(null)} />
    </>
  );
}

function MessageCard({ message: m, onEdit }: { message: AdminSystemMessage; onEdit: () => void }) {
  const queryClient = useQueryClient();
  const end = useMutation({
    mutationFn: () => api.endMessage(m.id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["messages"] }),
  });
  return (
    <li>
      <Card className="flex h-full flex-col gap-3" data-testid={`message-${m.title}`}>
        <SystemMessages messages={[m]} />
        <div className="flex flex-wrap gap-1.5">
          <Badge tone={m.state === "active" ? "success" : m.state === "scheduled" ? "warning" : "neutral"}>
            {m.state === "active" ? "showing now" : m.state}
          </Badge>
          <Badge>{m.audience === "ALL" ? "everyone" : `${m.recipientCount} customer(s)`}</Badge>
          {m.showOnLogin && <Badge>login page</Badge>}
          {m.dismissible && <Badge>{m.dismissedCount} closed it</Badge>}
        </div>
        <p className="text-xs text-muted">
          {formatDateTime(m.startsAt)} →{" "}
          {m.endedAt
            ? `ended ${formatDateTime(m.endedAt)}`
            : m.endsAt
              ? formatDateTime(m.endsAt)
              : "until ended"}
          {" · "}by {m.createdBy}
          {m.updatedBy ? ` · last change ${m.updatedBy}` : ""}
        </p>
        {m.state !== "ended" && (
          <div className="mt-auto flex flex-wrap gap-2">
            <Button size="sm" variant="secondary" onClick={onEdit}>
              Edit
            </Button>
            <Button size="sm" variant="danger" loading={end.isPending} onClick={() => end.mutate()}>
              End now
            </Button>
          </div>
        )}
        {end.error && <Notice tone="danger">{errorMessage(end.error)}</Notice>}
      </Card>
    </li>
  );
}

function MessageModal({
  message,
  onClose,
}: {
  message: AdminSystemMessage | "new" | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<Form>(emptyForm);
  const editing = message && message !== "new" ? message : null;
  useEffect(() => {
    if (message) setForm(message === "new" ? emptyForm() : formOf(message));
  }, [message]);
  const set = <K extends keyof Form>(key: K, value: Form[K]) => setForm((f) => ({ ...f, [key]: value }));

  const link = form.linkUrl.trim() ? checkMessageLink(form.linkUrl) : null;
  const recipients = form.recipients
    .split(/[\s,;]+/)
    .map((e) => e.trim())
    .filter(Boolean);
  const critical = form.level === "CRITICAL";
  const startOk = !Number.isNaN(new Date(form.startsAt).getTime());
  const endOk = !form.endsAt || new Date(form.endsAt) > new Date(form.startsAt);
  const valid =
    form.title.trim().length > 0 &&
    form.title.length <= L.title &&
    form.body.trim().length > 0 &&
    form.body.length <= L.body &&
    (!link || link.ok) &&
    form.linkLabel.length <= L.linkLabel &&
    (form.audience === "ALL" || recipients.length > 0) &&
    startOk &&
    endOk;

  const input = (): SystemMessageInput => ({
    title: form.title.trim(),
    body: form.body.trim(),
    level: form.level,
    audience: form.audience,
    recipients: form.audience === "USERS" ? recipients : [],
    linkUrl: form.linkUrl.trim() || null,
    linkLabel: form.linkLabel.trim() || null,
    dismissible: critical ? false : form.dismissible,
    showOnLogin: form.audience === "ALL" && form.showOnLogin,
    startsAt: new Date(form.startsAt).toISOString(),
    endsAt: form.endsAt ? new Date(form.endsAt).toISOString() : null,
  });

  return (
    <StepUpModal
      open={message !== null}
      onOpenChange={(o) => !o && onClose()}
      title={editing ? "Edit message" : "New message"}
      description="Customers see it at the start time; the change is in the audit log."
      confirmLabel={editing ? "Save message" : "Publish message"}
      canConfirm={valid}
      onConfirm={(code) =>
        editing ? api.updateMessage(editing.id, input(), code) : api.createMessage(input(), code)
      }
      onDone={() => void queryClient.invalidateQueries({ queryKey: ["messages"] })}
    >
      <div className="flex max-h-[55dvh] flex-col gap-4 overflow-y-auto pr-1">
        <Field label="Title" hint={`${form.title.length}/${L.title}`}>
          {(p) => (
            <Input
              {...p}
              value={form.title}
              maxLength={L.title}
              onChange={(e) => set("title", e.target.value)}
            />
          )}
        </Field>
        <Field label="Text" hint={`${form.body.length}/${L.body}`}>
          {(p) => (
            <Textarea
              {...p}
              rows={3}
              value={form.body}
              maxLength={L.body}
              onChange={(e) => set("body", e.target.value)}
            />
          )}
        </Field>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="Level">
            {(p) => (
              <Select
                {...p}
                value={form.level}
                onChange={(e) => set("level", e.target.value as Form["level"])}
              >
                <option value="INFO">Info</option>
                <option value="WARNING">Warning</option>
                <option value="CRITICAL">Critical (can&apos;t be closed)</option>
              </Select>
            )}
          </Field>
          <Field label="Who sees it">
            {(p) => (
              <Select
                {...p}
                value={form.audience}
                onChange={(e) => set("audience", e.target.value as Form["audience"])}
              >
                <option value="ALL">Everyone</option>
                <option value="USERS">Chosen customers</option>
              </Select>
            )}
          </Field>
        </div>
        {form.audience === "USERS" && (
          <Field label="Customer emails" hint={`One per line or comma-separated (max ${L.recipients}).`}>
            {(p) => (
              <Textarea
                {...p}
                rows={3}
                value={form.recipients}
                onChange={(e) => set("recipients", e.target.value)}
              />
            )}
          </Field>
        )}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field
            label="Link (optional)"
            hint={link?.ok ? `Opens ${link.host}` : "https:// only"}
            error={link && !link.ok ? link.reason : null}
          >
            {(p) => (
              <Input
                {...p}
                inputMode="url"
                placeholder="https://"
                value={form.linkUrl}
                onChange={(e) => set("linkUrl", e.target.value)}
              />
            )}
          </Field>
          <Field label="Button text">
            {(p) => (
              <Input
                {...p}
                placeholder="Learn more"
                maxLength={L.linkLabel}
                value={form.linkLabel}
                onChange={(e) => set("linkLabel", e.target.value)}
                disabled={!form.linkUrl.trim()}
              />
            )}
          </Field>
        </div>
        {link?.ok && (
          <Notice tone="warning">
            This link leaves the app and goes to <b>{link.host}</b>. Customers see that address on the button.
            Never link to pages that ask for passwords or recovery phrases.
          </Notice>
        )}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="Starts">
            {(p) => (
              <Input
                {...p}
                type="datetime-local"
                value={form.startsAt}
                onChange={(e) => set("startsAt", e.target.value)}
              />
            )}
          </Field>
          <Field label="Ends (optional)" error={endOk ? null : "Must be after the start"}>
            {(p) => (
              <Input
                {...p}
                type="datetime-local"
                value={form.endsAt}
                onChange={(e) => set("endsAt", e.target.value)}
              />
            )}
          </Field>
        </div>
        <label className={`flex min-h-11 items-center gap-3 text-sm ${critical ? "text-muted" : "text-fg"}`}>
          <input
            type="checkbox"
            className="size-5 accent-[var(--primary)]"
            checked={!critical && form.dismissible}
            disabled={critical}
            onChange={(e) => set("dismissible", e.target.checked)}
          />
          Customers can close it{critical ? " (not for critical messages)" : ""}
        </label>
        <label
          className={`flex min-h-11 items-center gap-3 text-sm ${form.audience === "ALL" ? "text-fg" : "text-muted"}`}
        >
          <input
            type="checkbox"
            className="size-5 accent-[var(--primary)]"
            checked={form.audience === "ALL" && form.showOnLogin}
            disabled={form.audience !== "ALL"}
            onChange={(e) => set("showOnLogin", e.target.checked)}
          />
          Also show on the login page (everyone only)
        </label>
        <div>
          <p className="mb-2 text-xs font-medium tracking-wide text-muted uppercase">Preview</p>
          <SystemMessages
            messages={[
              {
                id: "preview",
                title: form.title || "Title",
                body: form.body || "Text",
                level: form.level,
                link: link?.ok
                  ? { url: link.url, host: link.host, label: form.linkLabel.trim() || "Learn more" }
                  : null,
                dismissible: !critical && form.dismissible,
              },
            ]}
            onDismiss={() => undefined}
          />
        </div>
      </div>
    </StepUpModal>
  );
}
